// AI 機能の入口: 同意が無ければ同意ダイアログを開き、あれば実行する。
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { readComposite } from '@/app/engine'
import { pixelStore } from '@/pixels/pixelStore'
import { toast } from '@/composables/useToast'
import { t } from '@/i18n'
import { hasAiConsent, runMatting } from './runtime'
import { trimMask } from '@/selection/mask'
import type { CombineMode } from '@/selection/mask'

export type AiActionKind = 'selectSubject' | 'removeBackground'

/** 同意済みなら実行、未同意なら同意ダイアログを開いて保留 (ダイアログの OK 後に再実行される) */
export function requestAiAction(kind: AiActionKind): void {
  const ui = useUiStore()
  if (!hasAiConsent()) {
    ui.dialogArg = kind
    ui.dialog = 'aiConsent'
    return
  }
  void runAiAction(kind).catch(e => toast.error(t('ai.failed', { reason: e instanceof Error ? e.message : String(e) })))
}

/** 被写体を選択: 合成結果でマッティングして選択範囲にする */
export async function selectSubject(mode: CombineMode = 'new'): Promise<boolean> {
  const docStore = useDocumentStore()
  const doc = docStore.doc
  if (!doc) return false
  const c = readComposite()
  const gen = doc.pixelGeneration
  const alpha = await runMatting(c.rgba, c.width, c.height)
  if (!docStore.doc || docStore.doc.pixelGeneration !== gen) return false
  const mask = trimMask({ rect: { x: 0, y: 0, w: c.width, h: c.height }, data: alpha })
  docStore.combineSelection(mask, mode, 'history.aiSelect')
  return true
}

/** 背景を削除: アクティブなラスターレイヤーの画素でマッティングし、レイヤーマスクにする */
export async function removeBackground(layerId?: string): Promise<boolean> {
  const docStore = useDocumentStore()
  const doc = docStore.doc
  if (!doc) return false
  const id = layerId ?? doc.activeLayerIds[0]
  const layer = id ? docStore.rasterLayer(id) : undefined
  const s = layer ? pixelStore.get(layer.pixelId) : undefined
  if (!layer || !s) throw new Error(t('filter.needRaster'))
  const rgba = s.readRect({ x: -layer.offset.x, y: -layer.offset.y, w: doc.meta.width, h: doc.meta.height })
  const gen = doc.pixelGeneration
  const alpha = await runMatting(rgba, doc.meta.width, doc.meta.height)
  if (!docStore.doc || docStore.doc.pixelGeneration !== gen || !docStore.rasterLayer(layer.id)) return false
  docStore.setLayerMaskFromAlpha(layer.id, alpha, 'history.aiRemoveBackground')
  return true
}

export async function runAiAction(kind: AiActionKind): Promise<void> {
  const ui = useUiStore()
  const ok = kind === 'selectSubject' ? await selectSubject(ui.toolOptions.selectionMode) : await removeBackground()
  if (ok) toast.success(t(kind === 'selectSubject' ? 'ai.selected' : 'ai.removed'))
}
